import React from 'react';

function SCIsTutorial() {
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-green-600">
          <span role="img" aria-label="target">🎯</span> À quoi sert ce module ?
        </h3>
        <p className="text-justify leading-relaxed text-gray-700">
          Ce module vous permet d'identifier et de prospecter efficacement les SCI (Sociétés Civiles Immobilières) présentes dans les copropriétés que vous gérez.
        </p>
        
        <div className="rounded-xl border-2 border-green-500 bg-green-50 p-5 shadow-sm">
          <h5 className="mb-2 text-base font-bold text-green-800"><span role="img" aria-label="chart">📊</span> CAS N°1 : Proposition de biens en avant-première</h5>
          <p className="mb-3 text-sm leading-relaxed text-green-800">
            Lorsque vous avez un mandat de vente dans une copropriété où il y a des SCI, vous pouvez proposer le bien en avant-première aux SCI investisseuses avant de passer une annonce grand public. Ces SCI vous connaissent déjà puisque vous gérez leur copropriété et font souvent du rendement locatif.
          </p>
          <p className="mb-2 text-sm font-medium text-green-800">
            <strong>Comment procéder :</strong>
          </p>
          <ul className="ml-6 space-y-1 text-sm text-green-800">
            <li>Mettez le nom de l'immeuble dans le champ recherche</li>
            <li>Visualisez toutes les SCI présentes dans l'immeuble</li>
            <li>Faites une extraction Excel pour obtenir leurs coordonnées</li>
            <li>Utilisez le bouton "Modèles Mails / Courriers SCI / Rapports" pour leur faire votre proposition</li>
          </ul>
        </div>

        <div className="rounded-xl border-2 border-green-500 bg-green-50 p-5 shadow-sm">
          <h5 className="mb-2 text-base font-bold text-green-800"><span role="img" aria-label="briefcase">💼</span> CAS N°2 : Offres de services (transaction/gestion locative)</h5>
          <p className="mb-2 text-sm leading-relaxed text-green-800">
            Vous voulez proposer vos services de transaction ou de gestion locative à ces SCI. Les règles RGPD sont plus souples car une SCI est une personne morale, ce qui réduit les risques par rapport à la communication avec des copropriétaires personnes physiques.
          </p>
          <p className="text-sm text-green-800">
            Vous trouverez des modèles spécialisés pour ces campagnes dans le bouton "Modèles Mails / Courriers SCI / Rapports".
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-green-600">
          <span role="img" aria-label="check mark">✅</span> À FAIRE : Qualification de votre fichier SCI
        </h3>
        
        <div className="space-y-3">
          <h4 className="text-base font-semibold text-blue-600">A - Identifier vos interlocuteurs</h4>
          <div className="rounded-xl border-l-4 border-blue-400 bg-blue-50 p-5 text-blue-700 shadow-sm">
            En qualifiant chaque fiche SCI, vous pourrez envoyer un mail directement à la bonne personne avec l'offre que vous voulez lui faire. Que ce soit pour proposer un bien à la vente ou faire des offres commerciales sur vos activités de transaction/gestion locative.
          </div>

          <h4 className="text-base font-semibold text-blue-600">B - Qualifier vos cibles pour la gestion locative</h4>
          <div className="rounded-xl border-l-4 border-blue-400 bg-blue-50 p-5 text-blue-700 shadow-sm">
            En regardant le tableau, vous pouvez voir si la SCI possède d'autres biens que ceux qu'elle a dans les immeubles que vous gérez.
          </div>
          
          <p className="text-gray-700">Si c'est le cas :</p>
          <ul className="ml-6 space-y-2 text-gray-700">
            <li>Ouvrez chaque fiche détaillée des SCI de votre parc</li>
            <li>Visualisez où se situent géographiquement ces autres biens</li>
            <li>Vérifiez s'ils sont sur un territoire où vous pouvez proposer vos services de gestion locative</li>
            <li>Utilisez le bouton "CIBLÉ" dans le tableau ou sur la fiche détaillée pour indiquer si cette SCI est à contacter</li>
            <li>Utilisez les filtres sur la page générale pour extraire uniquement les SCI qui vous intéressent</li>
          </ul>

          <h4 className="text-base font-semibold text-blue-600">C - Qualifier vos cibles pour la transaction</h4>
          <div className="rounded-xl border-l-4 border-blue-400 bg-blue-50 p-5 text-blue-700 shadow-sm">
            Les SCI sont souvent des investisseurs actifs qui achètent et vendent régulièrement des biens immobiliers. Elles représentent donc un potentiel important pour vos activités de transaction.
          </div>
          
          <p className="text-gray-700">Comment identifier les SCI intéressantes pour la transaction :</p>
          <ul className="ml-6 space-y-2 text-gray-700">
            <li>Analysez le portfolio de biens de chaque SCI dans les fiches détaillées</li>
            <li>Identifiez les SCI avec de nombreux biens (potentiels vendeurs) ou en expansion (potentiels acheteurs)</li>
            <li>Regardez la localisation géographique de leurs biens pour cibler votre zone d'intervention</li>
            <li>Utilisez le bouton "Qualifier pour ciblage pour transaction" pour marquer les SCI prospects</li>
            <li>Créez des listes ciblées avec les filtres pour vos campagnes de prospection transaction</li>
            <li>Proposez-leur vos services d'agent immobilier pour leurs futurs projets d'achat/vente</li>
          </ul>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-green-600">
          <span role="img" aria-label="magnifying glass">🔍</span> Fonctionnalités principales
        </h3>
        <ul className="ml-6 space-y-2 text-gray-700">
          <li><strong>Recherche avancée :</strong> Filtrez par statut qualifié, ciblé, nombre de lots</li>
          <li><strong>Export Excel :</strong> Extractez les données pour vos campagnes</li>
          <li><strong>Fiches détaillées :</strong> Accédez aux informations complètes de chaque SCI</li>
          <li><strong>Modèles de communication :</strong> Courriers, emails et rapports prêts à personnaliser</li>
          <li><strong>Qualification des contacts :</strong> Enregistrez les interlocuteurs de chaque SCI</li>
        </ul>
      </div>
    </div>
  );
}

export default SCIsTutorial;


